import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { API_BASE_URL } from "../../config/apiConfig";

const SelectQuizIndividual = () => {
    const navigate = useNavigate();
    const [selectedExam, setSelectedExam] = useState({ subject: "", mode: "", testId: "" });
    const [selectedFilters, setSelectedFilters] = useState({
        year: "",
        subject: "",
        mode: "",
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [userResults, setUserResults] = useState([]);
    const [personData, setPersonData] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 10;

    // Teacher nav sets "student_id"; parent nav sets "child_id" — mirrors
    // pages/individual kid stats/index.js so this page works from either flow.
    const viewingContext = localStorage.getItem("viewing_context") || "parent";
    const apiBase = viewingContext === "teacher" ? "teachers" : "parents";
    const user_id = localStorage.getItem("student_id") || localStorage.getItem("child_id");
    const displayName = personData?.firstName || (viewingContext === "teacher" ? "The student" : "Your child");

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/${apiBase}/${user_id}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                });
                if (!response.ok) return;
                setPersonData(await response.json());
            } catch (error) {
                console.error("Error fetching profile:", error.message);
            }
        };
        fetchProfile();
    }, [apiBase, user_id]);

    useEffect(() => {
        const fetchUserResults = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/${apiBase}/user/${user_id}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || "Failed to fetch user results.");
                }

                const resultData = await response.json();
                setUserResults(resultData.data);
            } catch (error) {
                console.error("Error fetching results:", error.message);
                setError("Failed to fetch user results.");
            }
        };

        fetchUserResults();
    }, [apiBase, user_id]);

    const handleQuizSelection = (subject, mode, testId) => {
        const filteredResults = userResults.filter(
            (item) =>
                item.subject === subject &&
                item.mode === mode &&
                item.test_id === testId
        );

        if (filteredResults.length === 0) {
            setError("No results found for the selected criteria.");
            return;
        }

        navigate("/result", { state: { result: filteredResults } });
    };

    const goBack = () => {
        navigate(-1);
    };

    const getUniqueExamData = () => {
        const uniqueExamDataMap = new Map();

        userResults.forEach((item) => {
            const uniqueKey = `${item.subject}_${item.mode}_${item.test_id}`;
            if (!uniqueExamDataMap.has(uniqueKey)) {
                uniqueExamDataMap.set(uniqueKey, {
                    subject: item.subject,
                    mode: item.mode,
                    testId: item.test_id,
                    createdAt: item.createdAt,
                });
            }
        });

        return Array.from(uniqueExamDataMap.values());
    };

    const getFilterOptions = (key) => {
        const uniqueValues = new Set(
            userResults.map((item) =>
                key === "year"
                    ? new Date(item.createdAt).getFullYear()
                    : key === "subject"
                        ? item.subject
                        : item[key]
            )
        );
        return Array.from(uniqueValues);
    };

    const filteredExamData = getUniqueExamData()
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .filter((data) => {
            const year = new Date(data.createdAt).getFullYear();
            return (
                (!selectedFilters.year || year === parseInt(selectedFilters.year)) &&
                (!selectedFilters.subject || data.subject === selectedFilters.subject) &&
                (!selectedFilters.mode || data.mode === selectedFilters.mode)
            );
        });

    const totalPages = Math.max(1, Math.ceil(filteredExamData.length / PAGE_SIZE));
    const pagedExamData = filteredExamData.slice(
        (currentPage - 1) * PAGE_SIZE,
        currentPage * PAGE_SIZE
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedFilters]);

    return (
        <div className="flex flex-col min-h-screen bg-gray-100">
            <div className="flex items-center mt-6 ml-3">
                <button
                    onClick={goBack}
                    className="flex items-center gap-2 text-[#2148C0] hover:text-blue-600 font-medium transition"
                >
                    <FaArrowLeft className="text-xl" />
                    Back
                </button>
            </div>

            <main className="flex-grow flex flex-col items-center justify-center bg-gray-100 py-8">
                <h1 className="text-3xl text-center font-semibold text-[#2148C0] mb-6">
                    {displayName}'s Quiz Results
                </h1>

                {error && <p className="text-red-500 mb-4">{error}</p>}

                <div className="w-full max-w-4xl mb-6 flex gap-4">
                    <select
                        className="w-full p-2 border rounded-lg"
                        value={selectedFilters.year}
                        onChange={(e) =>
                            setSelectedFilters((prev) => ({
                                ...prev,
                                year: e.target.value,
                            }))
                        }
                    >
                        <option value="">All Years</option>
                        {getFilterOptions("year").map((year) => (
                            <option key={year} value={year}>
                                {year}
                            </option>
                        ))}
                    </select>

                    <select
                        className="w-full p-2 border rounded-lg"
                        value={selectedFilters.subject}
                        onChange={(e) =>
                            setSelectedFilters((prev) => ({
                                ...prev,
                                subject: e.target.value,
                            }))
                        }
                    >
                        <option value="">All Subjects</option>
                        {getFilterOptions("subject").map((type) => (
                            <option key={type} value={type}>
                                {type}
                            </option>
                        ))}
                    </select>

                    <select
                        className="w-full p-2 border rounded-lg capitalize "
                        value={selectedFilters.mode}
                        onChange={(e) =>
                            setSelectedFilters((prev) => ({
                                ...prev,
                                mode: e.target.value,
                            }))
                        }
                    >
                        <option className="captalize" value="">All Modes</option>
                        {getFilterOptions("mode").map((mode) => (
                            <option className="capitalize" key={mode} value={mode}>
                                {mode}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="w-full max-w-4xl">
                    <table className="table-auto w-full border-collapse border border-gray-300 shadow-lg">
                        <thead>
                            <tr className="bg-[#2148C0] text-white">
                                <th className="border border-gray-300 px-4 py-2">Date</th>
                                <th className="border border-gray-300 px-4 py-2">Subject</th>
                                <th className="border border-gray-300 px-4 py-2">Mode</th>
                                <th className="border border-gray-300 px-4 py-2">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pagedExamData.map((data, index) => (
                                <tr
                                    key={index}
                                    className={`hover:bg-blue-50 ${selectedExam.testId === data.testId ? "bg-blue-100" : ""
                                        }`}
                                >
                                    <td className="border border-gray-300 px-4 py-2 text-center">
                                        {new Date(data.createdAt).toLocaleDateString("en-US", {
                                            year: "numeric",
                                            month: "long",
                                            day: "numeric",
                                        })}
                                    </td>
                                    <td className="border border-gray-300 px-4 py-2 text-center">
                                        {data.subject}
                                    </td>
                                    <td className="border border-gray-300 px-4 py-2 text-center capitalize">
                                        {data.mode}
                                    </td>
                                    <td className="border border-gray-300 px-4 py-2 text-center">
                                        <button
                                            className="px-4 py-2 bg-[#2148C0] text-white rounded-lg hover:bg-blue-600 transition"
                                            onClick={() =>
                                                handleQuizSelection(data.subject, data.mode, data.testId)
                                            }
                                        >
                                            Check Result
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {filteredExamData.length > 0 && (
                        <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
                            <span>
                                Showing {(currentPage - 1) * PAGE_SIZE + 1}
                                –{Math.min(currentPage * PAGE_SIZE, filteredExamData.length)} of {filteredExamData.length}
                            </span>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    className="px-3 py-1.5 rounded-lg border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                                >
                                    Previous
                                </button>
                                <span>Page {currentPage} of {totalPages}</span>
                                <button
                                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                    className="px-3 py-1.5 rounded-lg border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </main>

            <footer className="text-center text-gray-300 py-4 bg-black bg-opacity-40">
                <p>&copy; {new Date().getFullYear()} TeeChaa CBT Application. All rights reserved.</p>
            </footer>
        </div>
    );
};

export default SelectQuizIndividual;